import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Droplets, Zap, Car, MapPin } from "lucide-react";
import { AREAS, PICKUP_AREAS, BIZ } from "@/data/business";
import { SectionHead, Reveal, EASE } from "./motion";

const FEATS = [
  { i: MapPin, t: "In-shop by appointment", d: BIZ.address },
  { i: Droplets, t: "Own water", d: "Fully self-contained mobile setup." },
  { i: Zap, t: "Own power", d: "No outlet or hose needed at your place." },
  { i: Car, t: "Pickup & drop-off", d: `Or a ride home / to work — ${PICKUP_AREAS.join(", ")}.` },
];

export const MobileSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section id="mobile" ref={ref} data-testid="mobile-detailing-section" className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-36">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <SectionHead eyebrow="03 — In-shop or at your door" lines={["We come", "to you."]}>
            In-shop services by appointment, or mobile detailing anywhere in the North Okanagan and beyond.
          </SectionHead>
          <div className="mt-12 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {FEATS.map((f, k) => (
              <Reveal key={f.t} delay={k * 0.08} className="bg-[#07080B] p-6 group">
                <f.i className="h-6 w-6 text-[#1bb4f2] transition-transform duration-500 group-hover:-translate-y-1" />
                <p className="mt-4 font-semibold">{f.t}</p>
                <p className="mt-1 text-sm text-slate-400">{f.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[16/11] lg:aspect-[4/5] [clip-path:polygon(0_0,100%_0,100%_88%,88%_100%,0_100%)]">
            <motion.img style={{ y, scale: 1.25 }} src="/img/garage.jpg" alt="Detailed car in a lit detailing bay" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07080B]/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 md:p-8">
              <p className="eyebrow mb-4">Service areas</p>
              <ul className="flex flex-wrap gap-2" data-testid="service-areas">
                {AREAS.map((a, k) => (
                  <motion.li key={a} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ ease: EASE, duration: 0.5, delay: k * 0.05 }}
                    className="px-3 py-1.5 border border-white/25 bg-black/40 backdrop-blur text-sm">{a}</motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
